import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { BillingFeature, NewReportSlideLayout } from 'webapp/enums/enums';

import CommonRequestTrialTextComponent from 'webapp/components/common/request-trial-text';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CommonUpgradePlanTextComponent from 'webapp/components/common/upgrade-plan-text';
import ToolTipComponent from 'webapp/components/tool-tip';

import type Promotion from 'webapp/models-custom/promotion';

interface Args {
	addNewSlideLayoutsDisabled: NewReportSlideLayout[];
	onSelect: (id: NewReportSlideLayout) => void;
	onClose?: () => void;
	isButtonVisible?: boolean;
}

interface SlideTypeOption {
	id: NewReportSlideLayout;
	text: string;
	img: string;
	isDisabled: boolean;
	tooltip: string;
	billingFeature?: BillingFeature;
}

class ReportsEditorSlideTypeComponent extends Component<Args> {
	@service('billing') declare billingService: Service.Billing;
	@service('model-manager/report-slides')
	declare mmReportSlidesService: Service.ModelManager.ReportSlides;
	@service('flow/promotions') declare flowPromotionsService: Service.Flow.Promotions;

	get addNewSlideLayoutsDisabled() {
		return this.args.addNewSlideLayoutsDisabled;
	}

	get options() {
		const options: SlideTypeOption[] = [];

		const slideTypes = [
			{ id: NewReportSlideLayout.COVER, text: 'Cover' },
			{ id: NewReportSlideLayout.BOARD, text: 'Dashboard' },
			{ id: NewReportSlideLayout.COLUMNS_1, text: '1 column' },
			{ id: NewReportSlideLayout.COLUMNS_2, text: '2 columns' },
			{ id: NewReportSlideLayout.COLUMNS_3, text: '3 columns' },
			{ id: NewReportSlideLayout.DATA_STORY, text: 'Data story' },
		];

		const disabledNewSlideLayoutsForCurrentPlan =
			this.mmReportSlidesService.disabledNewSlideLayoutsForCurrentPlan;
		let tooltip;

		slideTypes.forEach(({ id, text }) => {
			tooltip = '';
			if (this.addNewSlideLayoutsDisabled.includes(id) && !this.canRequestTrial) {
				tooltip = disabledNewSlideLayoutsForCurrentPlan[id]
					? 'Not available in current plan'
					: 'Maximum dashboards added';
			}
			options.push({
				id,
				text,
				img: this.getTypeImage(id),
				isDisabled: this.addNewSlideLayoutsDisabled.includes(id),
				tooltip,
				billingFeature: disabledNewSlideLayoutsForCurrentPlan[id],
			});
		});

		return options;
	}

	get availableTrialPromotion(): Promotion | undefined {
		const planForFeature = this.billingService.getPlanForTrial(
			BillingFeature.VISUALIZATIONS_ADVANCED,
		);
		return planForFeature
			? this.flowPromotionsService.getAvailableTrialPromotion(planForFeature)
			: undefined;
	}

	get canRequestTrial() {
		return this.billingService.canRequestTrial || !!this.availableTrialPromotion;
	}

	get showFakeButton() {
		return !!this.args.isButtonVisible;
	}

	@action
	select(id: NewReportSlideLayout): void {
		const slideData = this.options.find((item) => item.id === id);

		if (!slideData) {
			return;
		}

		if (slideData.billingFeature) {
			this.billingService.handleLimitations(slideData.billingFeature);
		} else if (!slideData.isDisabled) {
			this.args.onSelect(id);
		}

		if (this.args.onClose) {
			this.args.onClose();
		}
	}

	private getTypeImage(type: NewReportSlideLayout): string {
		let typeImage = '';
		switch (type) {
			case NewReportSlideLayout.BOARD:
				typeImage = 'board';
				break;
			case NewReportSlideLayout.COVER:
				typeImage = 'cover';
				break;
			case NewReportSlideLayout.COLUMNS_1:
				typeImage = 'text';
				break;
			case NewReportSlideLayout.COLUMNS_2:
				typeImage = 'side-by-side';
				break;
			case NewReportSlideLayout.COLUMNS_3:
				typeImage = 'columns-3';
				break;
			case NewReportSlideLayout.DATA_STORY:
				typeImage = 'story';
				break;
			default:
				typeImage = 'text';
		}
		return `/images/reports/slide-type-${typeImage}.svg`;
	}
	<template>
		<ul>
			{{#each this.options as |option|}}
				<li
					role="button"
					class="{{if option.isDisabled 'disabled'}}
						{{if option.billingFeature 'plan-limitation'}}"
					{{on "click" (fn this.select option.id)}}
				>
					<div class="img-container">
						<svg width="108" height="64">
							{{! @glint-ignore: xlink:href is not typed on SVGUseElement in Glint 2 }}
							<use xlink:href="{{option.img}}#img" width="100%" height="100%"></use>
						</svg>
						{{#if option.billingFeature}}
							<span class="plan-badge">
								<CommonUiIconComponent @name="StarFill" @size="sm" />
							</span>
						{{/if}}
					</div>
					{{#if option.isDisabled}}
						{{#if this.showFakeButton}}
							<div class="button">
								{{#if this.canRequestTrial}}
									<CommonRequestTrialTextComponent
										@feature={{option.billingFeature}}
									/>
								{{else}}
									<CommonUpgradePlanTextComponent
										@feature={{option.billingFeature}}
										@icon="ArrowUpCircleFill"
									/>
								{{/if}}
							</div>
						{{/if}}
					{{/if}}
					<span>{{option.text}}</span>
					<ToolTipComponent @text={{option.tooltip}} @maxWidth={{250}} @theme="light" />
				</li>
			{{/each}}
		</ul>
	</template>
}

export default ReportsEditorSlideTypeComponent;
